import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Shield, Globe, Lock, CheckCircle } from 'lucide-react';

interface DviNetworkOptInProps {
  shopId: string | null;
}

const SYSTEM_CATEGORIES = [
  'brakes',
  'suspension',
  'steering',
  'engine',
  'transmission',
  'cooling',
  'electrical',
  'exhaust',
  'tires',
  'body',
];

export function DviNetworkOptIn({ shopId }: DviNetworkOptInProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch current shop settings
  const { data: shop, isLoading } = useQuery({
    queryKey: ['shop-network-settings', shopId],
    queryFn: async () => {
      if (!shopId) return null;
      const { data, error } = await supabase
        .from('shops')
        .select('dvi_network_opt_in, dvi_network_exclusions')
        .eq('id', shopId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!shopId,
  });

  const handleOptInToggle = async (checked: boolean) => {
    if (!shopId) return;
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('shops')
        .update({ dvi_network_opt_in: checked })
        .eq('id', shopId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['shop-network-settings', shopId] });
      toast({
        title: checked ? 'Network Learning Enabled' : 'Network Learning Disabled',
        description: checked 
          ? 'Your anonymized data will contribute to industry insights' 
          : 'Your data will no longer be included in network aggregation',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExclusionToggle = async (category: string, excluded: boolean) => {
    if (!shopId) return;
    setIsUpdating(true);
    try {
      const currentExclusions = shop?.dvi_network_exclusions ?? [];
      const newExclusions = excluded
        ? [...currentExclusions, category]
        : currentExclusions.filter((c: string) => c !== category);

      const { error } = await supabase
        .from('shops')
        .update({ dvi_network_exclusions: newExclusions })
        .eq('id', shopId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['shop-network-settings', shopId] });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const isOptedIn = shop?.dvi_network_opt_in ?? false;
  const exclusions = shop?.dvi_network_exclusions ?? [];

  return (
    <div className="space-y-6">
      {/* Privacy Guarantees */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Privacy Guarantees
          </CardTitle>
          <CardDescription>
            Your data is protected with industry-standard privacy controls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">No Shop Identification</p>
                <p className="text-muted-foreground">Your shop is never identified in aggregated data</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">K-Anonymity Enforced</p>
                <p className="text-muted-foreground">Minimum 10 shops, 50 samples per signal</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">No Customer Data</p>
                <p className="text-muted-foreground">No PII or customer info is ever shared</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Statistical Only</p>
                <p className="text-muted-foreground">All data is rates and averages, never individual records</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Opt-In Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Network Intelligence Participation
          </CardTitle>
          <CardDescription>
            Contribute anonymized inspection data to improve industry-wide recommendations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="network-opt-in" className="text-base">
                Enable Network Learning
              </Label>
              <p className="text-sm text-muted-foreground">
                Your shop data is anonymized and aggregated with 10+ other shops
              </p>
            </div>
            <Switch
              id="network-opt-in"
              checked={isOptedIn}
              onCheckedChange={handleOptInToggle}
              disabled={isUpdating || isLoading}
            />
          </div>

          {isOptedIn && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Category Exclusions (optional)</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Exclude specific system categories from network sharing
              </p>
              <div className="flex flex-wrap gap-2">
                {SYSTEM_CATEGORIES.map(category => {
                  const isExcluded = exclusions.includes(category);
                  return (
                    <Badge
                      key={category}
                      variant={isExcluded ? 'secondary' : 'outline'}
                      className="cursor-pointer capitalize"
                      onClick={() => handleExclusionToggle(category, !isExcluded)}
                    >
                      {isExcluded && <Lock className="h-3 w-3 mr-1" />}
                      {category}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
